import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { SubscriptionPortalFlow } from "schemas/dashboard";
import { LIGHT_GREY } from "styles/oneOffColor";

import { fetcherAuth } from "utils/api";
import { useSubscription } from "utils/useSubscription";

import CancelQuestion from "./Question";
import {
  CancelScreen,
  DialogTitles,
  ProCancelQuestions,
  QuestionsToAsk,
  VaCancelQuestions,
} from "./schema";
import styles from "./styles";

interface Props {
  open: boolean;
  closeDialog: () => void;
}

const CancelFlow = ({ open, closeDialog }: Props) => {
  const { getToken } = useAuth();
  const { isProPlan, isVaPlan, plan, subscription } =
    useContext(SubscriptionContext);
  const { handleManagePlan } = useSubscription();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);

  const [screen, setScreen] = useState<CancelScreen>(CancelScreen.question);
  const [questionsToAsk, setQuestionsToAsk] = useState<QuestionsToAsk[]>([]);
  const [hasGiftedCredits, setHasGiftedCredits] = useState(false);
  const [transactionsInCents, setTransactionsInCents] = useState(0);
  const [mainLoading, setMainLoading] = useState(false);

  const resetState = () => {
    setScreen(CancelScreen.question);
    closeDialog();
  };

  const verifiedIfUserHasReceivedCredits = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-subscription-feedbacks/paid`,
        "GET",
      );
      if (res.paid) {
        setHasGiftedCredits(true);
      }
    } catch (error) {
      // Do nothing
    }
  };

  const handleChange = (question: string, answer: string) => {
    setQuestionsToAsk((prev) =>
      prev.map((questionToAsk) =>
        questionToAsk.question === question
          ? { ...questionToAsk, answer }
          : questionToAsk,
      ),
    );
  };

  const trackAnswer = async (isPrimaryButtonClicked: boolean) => {
    const buttonText = isPrimaryButtonClicked
      ? primaryTexts[screen]
      : secondaryTexts[screen];

    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-subscription-feedbacks/track`,
        "POST",
        {},
        {
          buttonText,
          screen,
          answers: questionsToAsk,
        },
      );
    } catch (error) {
      // Do nothing
    }
  };

  const setUpWithAccountManager = async () => {
    setScreen(CancelScreen.account_manager_success);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-subscription-feedbacks/notifications`,
        "POST",
        {},
        {
          answers: questionsToAsk,
        },
      );
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const grantCredits = async () => {
    setMainLoading(true);
    try {
      const { grantedCreditsInCents } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-subscription-feedbacks/grant-credits`,
        "POST",
      );
      if (!grantedCreditsInCents || grantedCreditsInCents <= 0) {
        resetState();
        return;
      }
      setTransactionsInCents(grantedCreditsInCents);
      setHasGiftedCredits(true);
      setScreen(CancelScreen.gifted_credits_granted);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setMainLoading(false);
    }
  };

  const nextPrimaryStep = {
    [CancelScreen.question]: resetState,
    [CancelScreen.account_manager]: setUpWithAccountManager,
    [CancelScreen.account_manager_success]: resetState,
    [CancelScreen.gifted_credits]: grantCredits,
    [CancelScreen.gifted_credits_granted]: resetState,
  };

  const nextSecondaryStep = {
    [CancelScreen.question]: () => {
      setScreen(CancelScreen.account_manager);
    },
    [CancelScreen.account_manager]: () => {
      if (!hasGiftedCredits) {
        setScreen(CancelScreen.gifted_credits);
      } else {
        handleManagePlan(plan!, SubscriptionPortalFlow.subscription_cancel);
      }
    },
    [CancelScreen.account_manager_success]: resetState,
    [CancelScreen.gifted_credits]: () => {
      handleManagePlan(plan!, SubscriptionPortalFlow.subscription_cancel);
    },
    [CancelScreen.gifted_credits_granted]: () => {
      handleManagePlan(plan!);
    },
  };

  const primaryTexts = {
    [CancelScreen.question]: "Keep Subscription",
    [CancelScreen.account_manager]: "Yes, set me up with an account manager!",
    [CancelScreen.account_manager_success]: "Close",
    [CancelScreen.gifted_credits]: "Accept This Offer",
    [CancelScreen.gifted_credits_granted]: "Close",
  };

  const secondaryTexts = {
    [CancelScreen.question]: "Next",
    [CancelScreen.account_manager]: "Continue to Cancel Subscription",
    [CancelScreen.account_manager_success]: "",
    [CancelScreen.gifted_credits]: "Cancel Subscription Anyway",
    [CancelScreen.gifted_credits_granted]: "View Next Invoice",
  };

  const primaryText = primaryTexts[screen];
  const secondaryText = secondaryTexts[screen];

  useEffect(() => {
    if (isProPlan) {
      setQuestionsToAsk(ProCancelQuestions);
    } else {
      setQuestionsToAsk(VaCancelQuestions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProPlan, isVaPlan]);

  useEffect(() => {
    if (currentOrg?.id) {
      verifiedIfUserHasReceivedCredits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const activeMonth = Number(subscription?.subscription?.totalMonthsSince || 0);
  const successMonth = Math.max(activeMonth + 1, 2);

  return (
    <Dialog
      maxWidth="sm"
      open={open}
      onClose={resetState}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 4,
          backgroundColor: LIGHT_GREY,
        },
      }}
    >
      <IconButton sx={styles.closeButton} onClick={resetState}>
        <Box component="i" className="fa-regular fa-xmark" />
      </IconButton>

      <DialogContent sx={{ mt: 3, px: { xs: 2, md: 8 } }}>
        <Typography variant="h4" textAlign={"center"} sx={{ mb: 3 }}>
          {DialogTitles[screen]}
        </Typography>

        {screen === CancelScreen.question &&
          questionsToAsk?.map((question, index) => (
            <CancelQuestion
              description={question.question}
              answer={question.answer}
              handleChange={handleChange}
              key={`cancel-question-${index}`}
            />
          ))}

        {screen === CancelScreen.account_manager && (
          <Typography textAlign={"center"} sx={{ mb: 3, fontSize: 16 }}>
            {isProPlan ? (
              <>
                Creators that send 100+ emails with a more personal email{" "}
                template to a diversity of brands are{" "}
                <strong>3x more likely</strong> to land brand deals.
                <br />
                <br />
              </>
            ) : (
              <>
                Creators who pitches with a more personal email template across
                several niches <strong>3x more likely</strong> to land brand
                deals.
                <br />
                <br />
              </>
            )}
            Do you want our account manager to help you make these changes?
          </Typography>
        )}

        {screen === CancelScreen.account_manager_success && (
          <Typography textAlign={"center"} sx={{ mb: 3, fontSize: 16 }}>
            We're diving into your account, pulling your metrics, and will send
            you an email with the findings in the next couple days. <br />
            <br />
            Hang tight! Your personal concierge is on it and moving fast!
          </Typography>
        )}

        {screen === CancelScreen.gifted_credits && (
          <>
            <Typography textAlign={"center"} sx={{ mb: 3, fontSize: 16 }}>
              Creators who stay active for{" "}
              <strong>{successMonth} months</strong> see much higher success
              than those who leave{" "}
              {activeMonth <= 0
                ? "within the first month"
                : `after ${activeMonth} month${activeMonth > 1 ? "s" : ""}`}
              .
            </Typography>
            <Typography sx={{ fontSize: 80 }} textAlign={"center"}>
              🎁
            </Typography>

            <Typography textAlign={"center"} sx={{ fontSize: 16 }}>
              Want 50% off your next month to stay active?
            </Typography>
          </>
        )}

        {screen === CancelScreen.gifted_credits_granted && (
          <>
            <Typography textAlign={"center"} sx={{ mb: 3, fontSize: 16 }}>
              You've received $
              <Box component="span" sx={{ fontSize: 30, color: "purple.main" }}>
                {Number(transactionsInCents / 100).toFixed(2)}
              </Box>{" "}
              credits for your next month! 💰
              <br />
              <br />
              It has been auto-applied to your next billing cycle. To view your
              next invoice, you can click on the button below.
            </Typography>
          </>
        )}
        <Grid
          container
          justifyContent={"center"}
          alignItems="center"
          sx={{ mt: 3 }}
          flexDirection={"column"}
        >
          {primaryText && (
            <LoadingButton
              sx={styles.primaryButton}
              variant="contained"
              disableElevation
              onClick={() => {
                trackAnswer(true);
                nextPrimaryStep[screen]();
              }}
              loading={mainLoading}
            >
              {primaryText}
            </LoadingButton>
          )}
          {secondaryText && secondaryText?.length > 0 && (
            <Button
              sx={styles.secondaryButton}
              onClick={() => {
                trackAnswer(false);
                nextSecondaryStep[screen]();
              }}
            >
              {secondaryText}
            </Button>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default CancelFlow;
